import { useState, useCallback } from 'react';

export const useDragAndDrop = (items, onUpdateOrder) => {
  const [draggedItem, setDraggedItem] = useState(null);
  const [dropPosition, setDropPosition] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = useCallback((e, item) => {
    e.stopPropagation();
    setDraggedItem(item);
    setIsDragging(true);
    
    // Set the drag data
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify(item));
    
    // Create a clone of the dragged element
    const clone = e.target.cloneNode(true);
    clone.style.position = 'fixed';
    clone.style.top = '0';
    clone.style.left = '0';
    clone.style.opacity = '0.8';
    clone.style.transform = 'scale(1.05)';
    clone.style.zIndex = '1000';
    clone.style.pointerEvents = 'none';
    clone.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
    clone.style.transition = 'transform 0.2s ease';
    clone.classList.add('dragged-clone');
    document.body.appendChild(clone);

    // Set the drag image to the clone
    e.dataTransfer.setDragImage(clone, 0, 0);
  }, []);

  const handleDragOver = useCallback((e, item, index) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    
    if (item !== draggedItem) {
      const rect = e.currentTarget.getBoundingClientRect();
      const mouseY = e.clientY;
      const elementMiddle = rect.top + rect.height / 2;
      
      setDropPosition({
        index,
        position: mouseY < elementMiddle ? 'above' : 'below'
      });
    }
  }, [draggedItem]);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const droppedItem = JSON.parse(e.dataTransfer.getData('text/plain'));
      
      if (droppedItem && dropPosition) {
        const itemsCopy = [...items];
        const draggedItemIndex = itemsCopy.findIndex(item => 
          item.category_details && item.category_details.id === droppedItem.id
        );
        const targetIndex = dropPosition.position === 'above' ? dropPosition.index : dropPosition.index + 1;

        if (draggedItemIndex !== -1) {
          const [removed] = itemsCopy.splice(draggedItemIndex, 1);
          itemsCopy.splice(targetIndex, 0, removed);

          // Update the order of all items
          const updatedItems = itemsCopy.map((item, index) => ({
            id: item.category_details.id,
            order: index
          }));

          try {
            await onUpdateOrder(updatedItems);
          } catch (error) {
            // Handle error silently
          }
        }
      }
    } catch (error) {
      // Handle error silently
    }
  }, [dropPosition, items, onUpdateOrder]);

  const handleDragEnd = useCallback(() => {
    // Remove the clone
    const clone = document.querySelector('.dragged-clone');
    if (clone) {
      clone.remove();
    }

    setDraggedItem(null);
    setDropPosition(null);
    setIsDragging(false);
  }, []);

  return {
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    draggedItem,
    dropPosition,
    isDragging
  };
}; 